import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { stanjeRobeStatusAPI } from '../services/api';
import { StanjeRobeCurrentStatusRow } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showCustomAlert } from './CustomAlert';

function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function formatNumber(n: number) {
  return Math.abs(round2(n)).toFixed(2);
}

function valueStyle(value: number) {
  if (Math.abs(value) < 0.005) return { color: '#64748b', fontWeight: 500 as const };
  if (value < 0) return { color: '#dc2626', fontWeight: 600 as const };
  return { color: '#15803d', fontWeight: 600 as const };
}

const StanjeRobeCurrentStatus: React.FC = () => {
  const { user } = useAuth();
  const { isCustomerPortal, t } = useLanguage();
  const isCustomer = user?.role === 'customer';
  const [rows, setRows] = useState<StanjeRobeCurrentStatusRow[]>([]);
  const [brandNames, setBrandNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await stanjeRobeStatusAPI.getCurrent();
      setRows(data.rows || []);
      setBrandNames(data.brandNames?.length ? data.brandNames : data.brandName ? [data.brandName] : []);
    } catch (err: any) {
      const msg = err?.response?.data?.message || (isCustomerPortal ? t('errors.loadStatus') : 'Greška pri učitavanju stanja');
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const numberBody = (key: 'currentKom' | 'currentPaleta' | 'currentM2', unitKey: 'unit.kom' | 'unit.paleta' | 'unit.m2') => (row: StanjeRobeCurrentStatusRow) => {
    const val = row[key];
    return (
      <span style={valueStyle(val)}>
        {formatNumber(val)}
        {isCustomerPortal && (
          <span className="stanje-robe-mobile-unit">{` ${t(unitKey)}`}</span>
        )}
      </span>
    );
  };

  const subtitle = isCustomer
    ? brandNames.length
      ? t('status.subtitleWithBrand', { brand: brandNames.join(', ') })
      : t('status.subtitleNoBrand')
    : 'Trenutno stanje = ulaz − izlaz (preračunato u m²)';

  const showBrandColumn = !isCustomer || brandNames.length > 1;

  return (
    <div className="p-3 md:p-4">
      <Card
        title={isCustomerPortal ? t('status.title') : 'Trenutno stanje robe'}
        subTitle={subtitle}
      >
        {isCustomer && brandNames.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {brandNames.map((name) => (
              <Tag key={name} value={name} severity="info" icon="pi pi-tag" />
            ))}
          </div>
        )}
        {error && <Message severity="error" text={error} className="mb-3" />}
        <div className="mb-3">
          <Button label={isCustomerPortal ? t('status.refresh') : 'Osveži'} icon="pi pi-refresh" outlined severity="secondary" onClick={load} loading={loading} />
        </div>
        <DataTable
          value={rows}
          loading={loading}
          paginator
          rows={15}
          emptyMessage={isCustomerPortal ? t('status.empty') : 'Nema podataka o stanju.'}
          className={isCustomer ? 'stanje-robe-current-status-table' : undefined}
          responsiveLayout={isCustomer ? 'stack' : undefined}
          breakpoint={isCustomer ? '768px' : undefined}
        >
          {showBrandColumn && <Column field="brandName" header={isCustomerPortal ? t('status.brand') : 'Brend'} sortable />}
          <Column field="productName" header={isCustomerPortal ? t('status.product') : 'Roba'} sortable />
          <Column
            header={isCustomerPortal ? t('unit.kom') : 'Trenutno (kom)'}
            body={numberBody('currentKom', 'unit.kom')}
          />
          <Column
            header={isCustomerPortal ? t('unit.paleta') : 'Trenutno (paleta)'}
            body={numberBody('currentPaleta', 'unit.paleta')}
          />
          <Column
            header={isCustomerPortal ? t('unit.m2') : 'Trenutno (m²)'}
            body={numberBody('currentM2', 'unit.m2')}
          />
        </DataTable>
      </Card>
    </div>
  );
};

export default StanjeRobeCurrentStatus;
